type Selector<T extends Record<any, any>, S> = (key: keyof T, elem: any) => S;
type Predicate<T extends Record<any, any>> = (key: keyof T, elem: any) => boolean;

export class ObjectUtils {
    static swap(objA: Record<any, any>, propA: keyof typeof objA, objB: Record<any, any>, propB: keyof typeof objB) {
        const tmp = objA[propA];
        objA[propA] = objB[propB];
        objB[propB] = tmp;
    }

    static keyOf<T>(object: Record<any, any>, value: T) {
        for (let key of Object.keys(object)) {
            if (object[key] === value) return key;
        }
        return undefined;
    }

    static select<T extends Record<any, any>, S>(object: T, selector: Selector<T, S>): Array<S> {
        const ret = [];
        for (let key of Object.keys(object)) {
            ret.push(selector(key, object[key]));
        }
        return ret;
    }

    static where<T extends Record<any, any>>(object: T, predicate: Predicate<T>): Partial<T> {
        const ret: Partial<T> = {};
        for (let key of Object.keys(object)) {
            if (predicate(key, object[key])) ret[key as keyof T] = object[key];
        }
        return ret;
    }

    static firstWhere<T extends Record<any, any>>(object: T, predicate: Predicate<T>): any | undefined {
        for (let key of Object.keys(object)) {
            if (predicate(key, object[key])) return object[key];
        }
        return undefined;
    }

    static firstKeyWhere<T extends Record<any, any>>(object: T, predicate: Predicate<T>): any | undefined {
        for (let key of Object.keys(object)) {
            if (predicate(key, object[key])) return key;
        }
        return undefined;
    }

    static find<T extends Record<any, any>, S>(object: T, prop: keyof T, value: S): S | undefined {
        for (let key in object) {
            if (object[key][prop] === value) return object[key];
        }
        return undefined;
    }

    static findKey<T extends Record<any, any>, S>(object: T, prop: keyof T, value: S): (keyof T) | undefined {
        for (let key in object) {
            if (object[key][prop] === value) return key;
        }
        return undefined;
    }

    static getFirst<T extends Record<any, any>, S>(object: T): S {
        return object[Object.keys(object)[0]];
    }

    static getFirstKey<T extends Record<any, any>>(object: T): keyof T {
        return Object.keys(object)[0];
    }

    static isObject(item: any): boolean {
        return (item && typeof item === "object" && !Array.isArray(item));
    }

    // https://stackoverflow.com/questions/27936772/how-to-deep-merge-instead-of-shallow-merge
    static deepMerge(target: Record<any, any>, ...sources: Array<Record<any, any>>): Record<any, any> {
        if (!sources.length) return target;
        const source = sources.shift();
        if (ObjectUtils.isObject(target) && ObjectUtils.isObject(source)) {
            for (const key in source) {
                if (ObjectUtils.isObject(source[key])) {
                    if (!target[key]) Object.assign(target, { [key]: {} });
                    ObjectUtils.deepMerge(target[key], source[key]);
                } else {
                    Object.assign(target, { [key]: source[key] });
                }
            }
        }
        return ObjectUtils.deepMerge(target, ...sources);
    }

    static pick<T extends Record<string, any>, K extends keyof T>(object: T, ...props: K[]): Pick<T, K> {
        const pick = {} as Pick<T, K>;
        for (const prop of props) {
            pick[prop] = object[prop];
        }
        return pick;
    }
}